const API_BASE_URL = import.meta.env.DEV
  ? "/api" // Use Vite proxy in development
  : import.meta.env.VITE_API_BASE_URL ||
    "https://netnest-internal-platform-backend-2dow.onrender.com/api";

class ApiService {
  // Haal teams (departementen) op van backend
  async getDepartments() {
    const response = await this.request("/departments");
    // Ondersteun zowel array als object response
    if (Array.isArray(response)) return response;
    if (response && Array.isArray(response.data)) return response.data;
    return [];
  }
  // Activeer een bedrijf
  async activateCompany(id) {
    const response = await this.request(`/companies/${id}/activate`, {
      method: "PATCH",
    });
    return response.success ? response.data : null;
  }

  // Deactiveer een bedrijf
  async deactivateCompany(id) {
    const response = await this.request(`/companies/${id}/deactivate`, {
      method: "PATCH",
    });
    return response.success ? response.data : null;
  }

  // Instellen als standaard
  async setDefaultCompany(id) {
    const response = await this.request(`/companies/${id}/set-default`, {
      method: "PATCH",
    });
    return response.success ? response.data : null;
  }

  // Algemene voorwaarden uploaden (multipart/form-data)
  async uploadTerms(id, formData) {
    const url = `${API_BASE_URL}/companies/${id}/terms`;
    const config = {
      method: "POST",
      body: formData,
      headers: {
        ...(this.authToken && { Authorization: `Bearer ${this.authToken}` }),
        ...(import.meta.env.PROD && { "X-Requested-With": "XMLHttpRequest" }),
      },
    };
    const response = await fetch(url, config);
    if (!response.ok) throw new Error("Uploaden van voorwaarden mislukt");
    return await response.json();
  }
  // Feedback op offerte versturen
  async sendOfferteFeedback(offerteId, { message, from }) {
    const url = `${API_BASE_URL}/public/offertes/${offerteId}/feedback`;
    const config = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(this.authToken && { Authorization: `Bearer ${this.authToken}` }),
        ...(import.meta.env.PROD && { "X-Requested-With": "XMLHttpRequest" }),
      },
      body: JSON.stringify({ message, from }),
    };
    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`,
        );
      }
      return await response.json();
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }
  // Email signature endpoints
  async getEmailSignature(userId) {
    const url = `${API_BASE_URL}/users/${userId}/email-signature`;
    const config = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(this.authToken && { Authorization: `Bearer ${this.authToken}` }),
        ...(import.meta.env.PROD && { "X-Requested-With": "XMLHttpRequest" }),
      },
    };
    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`,
        );
      }
      const data = await response.json();
      return data && data.emailSignature !== undefined
        ? data.emailSignature
        : "";
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  async updateEmailSignature(userId, emailSignature) {
    const url = `${API_BASE_URL}/users/${userId}/email-signature`;
    const config = {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(this.authToken && { Authorization: `Bearer ${this.authToken}` }),
        ...(import.meta.env.PROD && { "X-Requested-With": "XMLHttpRequest" }),
      },
      body: JSON.stringify({ emailSignature }),
    };
    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`,
        );
      }
      const data = await response.json();
      return data && data.emailSignature !== undefined
        ? data.emailSignature
        : "";
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  constructor() {
    this.authToken = null;
  }

  // Set authentication token
  setAuthToken(token) {
    this.authToken = token;
  }

  // E-mail templates ophalen
  async getEmailTemplates() {
    const response = await this.request("/email-templates");
    // Werkt met zowel array als object response
    if (Array.isArray(response)) return response;
    if (response && Array.isArray(response.data)) return response.data;
    return [];
  }

  // Haal beschikbare functies (functietitels) op van backend
  async getFunctions() {
    const response = await this.request("/functions");
    if (!response) return [];
    // Support both { success: true, data: { functions: [...] } } and direct array
    if (Array.isArray(response)) return response;
    if (
      response.success &&
      response.data &&
      Array.isArray(response.data.functions)
    )
      return response.data.functions;
    if (response.data && Array.isArray(response.data)) return response.data;
    return [];
  }

  // Clear authentication token
  clearAuthToken() {
    this.authToken = null;
  }

  // Helper method for GET requests
  async get(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "GET",
    });
  }

  // Helper method for POST requests
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Helper method for PUT requests
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // Helper method for DELETE requests
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "DELETE",
    });
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        "Content-Type": "application/json",
        // Add auth token if available
        ...(this.authToken && {
          Authorization: `Bearer ${this.authToken}`,
        }),
        // Add origin header for CORS debugging
        ...(import.meta.env.PROD && { "X-Requested-With": "XMLHttpRequest" }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Try to parse structured JSON error body and surface a concise message
        const bodyText = await response.text();
        let friendlyMessage = response.statusText || `HTTP ${response.status}`;

        try {
          const json = JSON.parse(bodyText || "{}");
          // Prefer `message`, then `error`, then validation `details`
          if (json.message) {
            friendlyMessage = json.message;
          } else if (json.error) {
            friendlyMessage = json.error;
          } else if (Array.isArray(json.details) && json.details.length > 0) {
            // details typically from express-validator: pick first message or join
            const msgs = json.details.map((d) => d.msg || JSON.stringify(d));
            friendlyMessage = msgs.join(", ");
          }
        } catch (parseErr) {
          // Not JSON — fall back to raw text
          if (bodyText && bodyText.trim()) friendlyMessage = bodyText;
        }

        console.error("API Error Response:", {
          status: response.status,
          statusText: response.statusText,
          body: bodyText,
          url: url,
          friendlyMessage,
        });

        throw new Error(friendlyMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  // Companies endpoints
  async getCompanies() {
    const response = await this.request("/companies");
    return response.success ? response.data : [];
  }

  async getCompany(id) {
    const response = await this.request(`/companies/${id}`);
    return response.success ? response.data : null;
  }

  async createCompany(companyData) {
    const response = await this.request("/companies", {
      method: "POST",
      body: JSON.stringify(companyData),
    });
    return response.success ? response.data : null;
  }

  async updateCompany(id, companyData) {
    const response = await this.request(`/companies/${id}`, {
      method: "PUT",
      body: JSON.stringify(companyData),
    });
    return response.success ? response.data : null;
  }

  async deleteCompany(id) {
    const response = await this.request(`/companies/${id}`, {
      method: "DELETE",
    });
    return response.success;
  }

  // Entities/Contacts endpoints (for companies/organizations)
  async getEntities() {
    const response = await this.request("/entities");
    return response.success ? response.data : [];
  }

  async getEntity(id) {
    const response = await this.request(`/entities/${id}`);
    return response.success ? response.data : null;
  }

  async createEntity(entityData) {
    const response = await this.request("/entities", {
      method: "POST",
      body: JSON.stringify(entityData),
    });
    return response.success ? response.data : null;
  }

  async updateEntity(id, entityData) {
    const response = await this.request(`/entities/${id}`, {
      method: "PUT",
      body: JSON.stringify(entityData),
    });
    return response.success ? response.data : null;
  }

  async deleteEntity(id) {
    const response = await this.request(`/entities/${id}`, {
      method: "DELETE",
    });
    return response.success;
  }

  // Contacts endpoints
  async getContacts(query = {}) {
    let url = "/contacts";
    const params = new URLSearchParams();
    if (query.segment) params.append("segment", query.segment);
    if (query.search) params.append("search", query.search);
    if (query.isActive !== undefined) params.append("isActive", query.isActive);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await this.request(url);
    return response.success ? response.data : [];
  }

  async getContact(id) {
    const response = await this.request(`/contacts/${id}`);
    return response.success ? response.data : null;
  }

  async createContact(contactData) {
    const response = await this.request("/contacts", {
      method: "POST",
      body: JSON.stringify(contactData),
    });
    return response.success ? response.data : null;
  }

  async updateContact(id, contactData) {
    const response = await this.request(`/contacts/${id}`, {
      method: "PUT",
      body: JSON.stringify(contactData),
    });
    return response.success ? response.data : null;
  }

  async deleteContact(id) {
    const response = await this.request(`/contacts/${id}`, {
      method: "DELETE",
    });
    return response.success;
  }

  async activateContact(id) {
    const response = await this.request(`/contacts/${id}/activate`, {
      method: "PATCH",
    });
    return response.success ? response.data : null;
  }

  async deactivateContact(id) {
    const response = await this.request(`/contacts/${id}/deactivate`, {
      method: "PATCH",
    });
    return response.success ? response.data : null;
  }

  async getContactsBySegment(segment) {
    const response = await this.request(`/contacts/segment/${segment}`);
    return response.success ? response.data : [];
  }

  // Influencers endpoints
  async getInfluencers() {
    const response = await this.request("/influencers");
    // Handle both wrapped and direct response formats
    if (response.success !== undefined) {
      return response.success ? response.data : response;
    }
    // If no success property, assume direct data response
    return Array.isArray(response) ? response : [];
  }

  async getInfluencer(id) {
    const response = await this.request(`/influencers/${id}`);
    // Handle both wrapped and direct response formats
    if (response.success !== undefined) {
      return response.success ? response.data : null;
    }
    // If no success property, assume direct data response
    return response;
  }

  async createInfluencer(influencerData) {
    const response = await this.request("/influencers", {
      method: "POST",
      body: JSON.stringify(influencerData),
    });
    // Handle both wrapped and direct response formats
    if (response.success !== undefined) {
      return response.success ? response.data : response;
    }
    // If no success property, assume direct data response
    return response;
  }

  async updateInfluencer(id, influencerData) {
    const response = await this.request(`/influencers/${id}`, {
      method: "PUT",
      body: JSON.stringify(influencerData),
    });
    // Handle both wrapped and direct response formats
    if (response.success !== undefined) {
      return response.success ? response.data : null;
    }
    // If no success property, assume direct data response
    return response;
  }

  async deleteInfluencer(id) {
    const response = await this.request(`/influencers/${id}`, {
      method: "DELETE",
    });
    // Handle both wrapped and direct response formats
    if (response.success !== undefined) {
      return response.success;
    }
    // If no success property, assume success if no error was thrown
    return true;
  }

  // Artists endpoints
  async getArtists() {
    const response = await this.request("/artists");
    if (response.success !== undefined) {
      return response.success ? response.data : response;
    }
    return Array.isArray(response) ? response : [];
  }

  async getArtist(id) {
    const response = await this.request(`/artists/${id}`);
    if (response.success !== undefined) {
      return response.success ? response.data : null;
    }
    return response;
  }

  async getArtistHistory(id) {
    const response = await this.request(`/artists/${id}/history`);
    if (response.success !== undefined) {
      return response.success ? response.data : [];
    }
    return Array.isArray(response) ? response : [];
  }

  historyPreviewSrc(artistId, historyId) {
    return `/api/artists/${artistId}/history/${historyId}/preview`;
  }

  async deleteArtistHistory(artistId, historyId) {
    const response = await this.request(
      `/artists/${artistId}/history/${historyId}`,
      { method: "DELETE" },
    );
    if (response.success !== undefined) {
      return response.success;
    }
    return true;
  }

  async createArtist({ domain }) {
    const response = await this.request("/artists", {
      method: "POST",
      body: JSON.stringify({ domain }),
    });
    if (response.success !== undefined) {
      return response.success ? response.data : response;
    }
    return response;
  }

  async reanalyzeArtist(id) {
    const response = await this.request(`/artists/${id}/analyze`, {
      method: "POST",
    });
    if (response.success !== undefined) {
      return response.success ? response.data : response;
    }
    return response;
  }

  async updateArtist(id, data) {
    const response = await this.request(`/artists/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    if (response.success !== undefined) {
      return response.success ? response.data : response;
    }
    return response;
  }

  async deleteArtist(id) {
    const response = await this.request(`/artists/${id}`, {
      method: "DELETE",
    });
    if (response.success !== undefined) {
      return response.success;
    }
    return true;
  }

  // E-mail bulk endpoint
  async sendBulkEmail(data, isFormData = false) {
    if (isFormData) {
      // FormData: geen Content-Type header zetten!
      const url = `${API_BASE_URL}/email/bulk`;

      const config = {
        method: "POST",
        body: data,
        // headers niet zetten, fetch regelt boundary!
        headers: {
          ...(this.authToken && {
            Authorization: `Bearer ${this.authToken}`,
          }),
          ...(import.meta.env.PROD && { "X-Requested-With": "XMLHttpRequest" }),
        },
        credentials: "include",
      };
      try {
        const response = await fetch(url, config);
        if (!response.ok) {
          const errorText = await response.text();
          console.error("❌ [ApiService] API Error Response:", errorText);
          throw new Error(
            `HTTP error! status: ${response.status} - ${errorText}`,
          );
        }
        const result = await response.json();
        return result;
      } catch (error) {
        console.error("❌ [ApiService] FormData request failed:", error);
        throw error;
      }
    } else {
      try {
        const result = await this.request("/email/bulk", {
          method: "POST",
          body: JSON.stringify(data),
        });
        return result;
      } catch (error) {
        console.error("❌ [ApiService] JSON request failed:", error);
        throw error;
      }
    }
  }

  // Haal alle deals voor een project op
  async getDealsByProject(projectId) {
    const response = await this.request(`/deals?projectId=${projectId}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  // Haal alle offertes voor een deal op
  async getOffertesByDeal(dealId) {
    const response = await this.request(`/offertes?dealId=${dealId}`);
    return Array.isArray(response) ? response : response.data || [];
  }
}

export default new ApiService();
